import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import { getToken } from "@/core/services/JwtService";
import { currentTenant } from "@/core/services/TenantService";
import { useApiClient } from "./useApiClient";
import { h } from "vue";
/**
 * Các hàm làm việc với storage
 * @returns
 */
export const useStorage = () => {
    const config = useRuntimeConfig();
    const apiClient = useApiClient();

    /**
     * @description Upload file
     * @param file
     * @returns
     */
    const upload = (file: File, folderId: number = 0, metas: Object = {}) => {
        return new Promise((resolve, reject) => {
            const { $toast, $getStorageUrl } = useNuxtApp();
            const uppy = new Uppy({
                meta: { type: "avatar" },
                restrictions: { maxNumberOfFiles: 1 },
                autoProceed: false,
            });
            uppy.use(Tus, {
                endpoint: config.public.storageUrl,
                removeFingerprintOnSuccess: true,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                chunkSize: 1 * 1024 * 1024,
                headers: {
                    Authorization: `Bearer ${getToken()}`,
                },
                onAfterResponse: function (request, response) {
                    var imageUrls = response.getHeader('X-Image-Urls');
                    var path2;
                    if (imageUrls) {
                        path2 = imageUrls.split(',').map((url) => $getStorageUrl(url));
                    }
                    var path = response.getHeader('X-File-Path');
                    if (path) {
                        setTimeout(() => {
                            $toast.remove(loadingId);
                            $toast.success("Tải lên thành công", {
                                position: "bottom-left",
                            });
                        }, 100);
                        // resolve về 2 giá trị: path và path2
                        if(path2 && path2.length > 0){
                            return resolve({
                                path: $getStorageUrl(path),
                                paths: path2,
                            });
                        }else{
                            return resolve($getStorageUrl(path));
                        }
                    }
                }
            });
            uppy.on("file-added", (file) => {
                uppy.setFileMeta(file.id, {
                    name: file.name,
                    contentType: file.type || "application/octet-stream",
                    tenantId: currentTenant(),
                    folderId: folderId,
                    ...metas
                });
            });
            uppy.addFile({
                name: file.name,
                type: file.type,
                data: file,
            });
            const loadingId = $toast.loading("Đang tải lên...", {
                type: "default",
                closeOnClick: false,
                autoClose: false,
                isLoading: true,
                position: "bottom-left",
            });
            uppy.on("upload-progress", (file, progress) => {
                // file: { id, name, type, ... }
                // progress: { uploader, bytesUploaded, bytesTotal }
                $toast.update(loadingId, {
                    render: () => {
                        return h(
                            "div",
                            `Đang tải lên... ${file.name} - ${Math.round((progress.bytesUploaded / progress.bytesTotal) * 100)}%`
                        );
                    },
                });
            });
            uppy.upload();
        });
    };
    const uploadFile = (file: File, folderId: number = 0, metas: Object = {}) => {
        return new Promise((resolve, reject) => {
            const { $toast, $getStorageUrl } = useNuxtApp();
            const uppy = new Uppy({
                meta: { type: "avatar" },
                restrictions: { maxNumberOfFiles: 1 },
                autoProceed: false,
            });
            uppy.use(Tus, {
                endpoint: config.public.storageUrl,
                removeFingerprintOnSuccess: true,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                chunkSize: 1 * 1024 * 1024,
                headers: {
                    Authorization: `Bearer ${getToken()}`,
                },
                onAfterResponse: function (request, response) {
                    var path = response.getHeader('X-File-Path');
                    if (path) {
                        setTimeout(() => {
                            $toast.remove(loadingId);
                            $toast.success("Tải lên thành công", {
                                position: "bottom-left",
                            });
                        }, 100);
                        return resolve(response);
                    }
                }
            });
            uppy.on("file-added", (file) => {
                uppy.setFileMeta(file.id, {
                    name: file.name,
                    contentType: file.type || "application/octet-stream",
                    tenantId: currentTenant(),
                    folderId: folderId,
                    ...metas
                });
            });
            uppy.addFile({
                name: file.name,
                type: file.type,
                data: file,
            });
            const loadingId = $toast.loading("Đang tải lên...", {
                type: "default",
                closeOnClick: false,
                autoClose: false,
                isLoading: true,
                position: "bottom-left",
            });
            uppy.on("upload-progress", (file, progress) => {
                // file: { id, name, type, ... }
                // progress: { uploader, bytesUploaded, bytesTotal }
                $toast.update(loadingId, {
                    render: () => {
                        return h(
                            "div",
                            `Đang tải lên... ${file.name} - ${Math.round((progress.bytesUploaded / progress.bytesTotal) * 100)}%`
                        );
                    },
                });
            });
            uppy.upload();
        });
    };
    const base64ToBlob = (base64: string, type: string) => {
        const binStr = atob(base64.split(",")[1]);
        const len = binStr.length;
        const arr = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
        }
        return new Blob([arr], { type: type });
    };
    /**
     * @description Upload base64 image
     * @param base64
     * @param filename
     * @returns
     */
    const uploadBase64 = (
        base64: string,
        filename: string,
        mimeType: string = "image/png",
        folderId: number = 0,
        metas: Object = {}
    ) => {
        var blob = base64ToBlob(base64, mimeType);
        var file = new File([blob], filename, { type: mimeType });
        return upload(file, folderId, metas);
    };
    /**
     * @description Upload image by url
     * @param url
     * @returns
     */
    const uploadByUrl = (url: string) => {
        return new Promise((resolve, reject) => {
            apiClient
                .uploadByUrl(url)
                .then((response) => {
                    if (response.success && response.data)
                        return resolve(response.data.path);
                    return reject(response.message);
                })
                .catch((error) => {
                    return reject(error);
                });
        });
    };

    return {
        upload,
        uploadFile,
        uploadBase64,
        uploadByUrl,
    };
};
